/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Internal dependencies
import { ThemeContext, getBreakpointIDs } from '@rakuten/contexts/ThemeContext';
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import { View } from '@rakuten/modules-core-components';
import Label, { FONT_WEIGHT, TYPE as LABEL_TYPE } from './../atoms/Label';
import ModalBlocker from './../atoms/ModalBlocker';
import Spacer from './../atoms/Spacer';
import IconButton, { DISPLAY_VARIANT } from './IconButton';


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

/**
 * Calculate styles for this component, optionally based on props, theme,
 * currently matching breakpoint etc.
 *
 * @param {object} args Arguments used for style calculation
 * @param {object} args.props Current props passed to the component
 * @param {object} args.theme Current theme object
 * @param {object} args.breakpoint Currently matching breakpoint
 * @return {object} Object that maps style names to style objects
 */
function createStyles({ props, theme, breakpoint }) {
  const basicUnit = theme.mixins.basicUnit;
  const themeBreakpoints = getBreakpointIDs();

  return {
    container: {
      backgroundColor: theme.colors.backgroundUI,
      borderRadius: breakpoint.id === themeBreakpoints.XSMALL
        ? basicUnit(3)
        : basicUnit(4),
      boxShadow: theme.mixins.boxShadow(2, 3),
      display: 'flex',
      flexDirection: 'column',
      left: '50%',
      maxHeight: breakpoint.id === themeBreakpoints.XSMALL
        ? '80%'
        : basicUnit(72.5),
      maxWidth: basicUnit(63.5),
      minWidth: basicUnit(34),
      paddingLeft: props.disableHorizontalPadding ? 0 : basicUnit(2),
      paddingRight: props.disableHorizontalPadding ? 0 : basicUnit(2),
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: theme.variables.zIndexDialogs
    },

    titleContainer: {
      ...(props.disableHorizontalPadding ? {} : theme.mixins.paddingAdjustHelper('small', 'x')),

      alignItems: 'center',
      display: 'flex',
      minHeight: basicUnit(6),
      paddingLeft: props.showCloseIcon ? 0 : basicUnit(2)
    },

    contentContainer: {
      ...theme.mixins.scrollable('y'),

      minHeight: basicUnit(6),
      paddingTop: !props.title && !props.showCloseIcon
        ? basicUnit(2)
        : 0,

      marginBottom: basicUnit(),
      marginTop: basicUnit()
    },

    actionsContainer: {
      alignItems: 'center',
      display: 'flex',
      paddingLeft: props.disableHorizontalPadding ? basicUnit(2) : 0,
      paddingRight: props.disableHorizontalPadding ? basicUnit(2) : 0,
      flexDirection: breakpoint.id === themeBreakpoints.XSMALL
        ? 'column'
        : 'row',
      justifyContent: breakpoint.id === themeBreakpoints.XSMALL
        ? 'space-evenly'
        : 'flex-end',
      minHeight: breakpoint.id === themeBreakpoints.XSMALL
        ? basicUnit(12)
        : basicUnit(8)
    }
  };
}


// -----------------------------------------------------------------------------
// DIALOG COMPONENT
// -----------------------------------------------------------------------------

function Dialog(props) {
  const context = useContext(ThemeContext);
  const breakpoint = context.breakpoint;

  const dialogRef = useRef();

  // "Hook" this component into the theme/style system
  const [ styles ] = useStyleSystem(
    {
      id: 'Dialog',
      props,
      shouldWatchBreakpoint: true,
      styleFn: createStyles
    }
  );

  const { t } = useTranslation();

  // Get focus as soon as dialog is rendered
  useEffect(() => {
    if (props.autoFocus && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [ dialogRef.current ]);


  const onKeyDown = function onKeyDown(e) {
    if (e.key.toLowerCase() === 'escape' && props.closeAction) {
      props.closeAction();
    }
  };


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  // Force small action buttons.
  // Force full width action buttons on small screens
  let primary = props.primaryActionButton;
  let secondary = props.secondaryActionButton;
  const newProps = { size: 'small' };
  if (breakpoint.id === getBreakpointIDs().XSMALL) {
    newProps.fullWidth = true;
  }
  if (props.primaryActionButton) {
    primary = React.cloneElement(props.primaryActionButton, newProps);
  }
  if (props.secondaryActionButton) {
    secondary = React.cloneElement(props.secondaryActionButton, newProps);
  }

  return (
    <>
      <ModalBlocker />
      <View
        accessible={ true }
        accessibilityRole={ 'dialog' }
        accessibilityLabel= { props.title}
        accessibilityViewIsModal={ true }
        style={[styles.container, props.style]}
        // Close popup via escape key
        onKeyDown={ onKeyDown }
        ref={dialogRef}
      >
        {
          (props.title || props.showCloseIcon) &&
            <View style={ styles.titleContainer }>
              {
                props.showCloseIcon &&
                  <IconButton
                    accessibilityLabel={  t("Close dialog", { ns: 'a11y' }) }
                    displayVariant={ DISPLAY_VARIANT.STANDARD_FILLED }
                    icon="close-big"
                    onPress={ props.closeAction }
                  />
              }
              {
              props.title &&
                  <Label
                    texttype= { 'h2' }
                    fontWeight={ FONT_WEIGHT.BOLD }
                    ignoreLineHeight={ true }
                    text={ props.title }
                    type={ LABEL_TYPE.TEXTLARGE }
                  />
              }
            </View>
        }
        <View style={ styles.contentContainer }>
          { props.children }
        </View>
        <View style={ styles.actionsContainer }>
          { secondary }
          <Spacer width={ .8 } />
          { primary }
        </View>
      </View>
    </>
  );
}


// -----------------------------------------------------------------------------
// PROPS VALIDATION
// -----------------------------------------------------------------------------

Dialog.propTypes = {
  // Items/Elements that will be shown as the dialog's content [optional]
  children: PropTypes.arrayOf(PropTypes.element),

  // Function to call when the close icon is pressed [optional]
  closeAction: PropTypes.func,

  // Disable the horizontal padding [optional]
  disableHorizontalPadding: PropTypes.bool,

  // A Button component
  primaryActionButton: PropTypes.element.isRequired,

  // A Button component to show beside the primary button
  secondaryActionButton: PropTypes.element,

  // Whether or not to show the close icon
  showCloseIcon: PropTypes.bool,

  // The title/headline of the dialog
  title: PropTypes.string
};

Dialog.defaultProps = {
  autoFocus: true,
  disableHorizontalPadding: false,
  showCloseIcon: true
};


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default Dialog;
